import { useTranslation } from "react-i18next";
import Button from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useRef } from "react";
import { useToast } from "@/features/toast";
import useAuthStore from "@/store/use-auth-store";
import { Link, useNavigate } from "react-router-dom";
import { EyeIcon, EyeOffIcon, LockIcon, UserIcon } from "lucide-react";
import { AuthStage } from "@/components/auth/auth-stage";
import {
  authFieldClass,
  authFooterClass,
  authFormClass,
  authLabelClass,
  authLinkClass,
  authPrimaryButtonClass,
  authSocialStackClass,
  authStackClass,
  authSubtitleClass,
  authTitleClass,
} from "@/components/auth/auth-ui";
import { AuthSupportLink } from "@/components/auth/auth-support-link";
import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import {
  GoogleBrandIcon,
  AppleBrandIcon,
} from "@/components/icons/social-icons";
import { OmuzLogo } from "@/components/icons/harmony-icons-inline";
import { SocialButton } from "@/components/ui/social-button";
import { FormDivider } from "@/components/ui/form-divider";
import { miniAppBridgeService } from "@/api/services";
import mixpanel from "mixpanel-browser";
import * as Sentry from "@sentry/react";
import { NetmeraLoginFailedEvent } from "@/features/auth";
import {
  consumeAuthGateRedirect,
  readLastVisitedRoute,
} from "@/features/auth-gate";
import { useAppleSignIn } from "@/hooks";

export default function LoginPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isMiniAppUser, setIsMiniAppUser] = useState(false);
  const { success, error, info, warning } = useToast();
  const { setUser, setTokens } = useAuthStore();
  const navigate = useNavigate();
  const { signIn: handleAppleLogin, isLoading: isAppleLoading } =
    useAppleSignIn("login");
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const isSubmitting = useRef(false);

  // MiniApp integration - check shell user and handle accordingly
  useEffect(() => {
    const handleMiniAppLogin = async () => {
      if (!miniAppBridgeService.isInMiniApp()) {
        return;
      }

      // Check if we've already done the miniapp redirect check
      const miniAppCheckDone = sessionStorage.getItem(
        "miniapp_login_check_done",
      );
      if (miniAppCheckDone) {
        return;
      }

      try {
        // Get email from shell app
        const shellEmail = await miniAppBridgeService.getUserEmail();

        // If no email in shell app, redirect to register
        if (!shellEmail) {
          sessionStorage.setItem("miniapp_login_check_done", "true");
          navigate("/register", { state: { fromMiniApp: true } });
          return;
        }

        // Check if user exists in our database
        try {
          const response = await apiClient.get<{
            success: boolean;
            data: { exists: boolean };
          }>(
            `${API_ENDPOINTS.checkUserExists}?email=${encodeURIComponent(
              shellEmail,
            )}`,
          );

          if (!response.data?.exists) {
            // User doesn't exist in our DB, redirect to register
            sessionStorage.setItem("miniapp_login_check_done", "true");
            navigate("/register", {
              state: { fromMiniApp: true, email: shellEmail },
            });
            return;
          }

          // User exists, fill email and focus on password
          setEmail(shellEmail);
          setIsMiniAppUser(true);
          sessionStorage.setItem("miniapp_login_check_done", "true");

          // Focus on password field after a short delay
          setTimeout(() => {
            passwordInputRef.current?.focus();
          }, 100);
        } catch (apiError) {
          // If API call fails, still fill email and let user proceed
          console.warn("MiniApp: Failed to check user existence:", apiError);
          setEmail(shellEmail);
          setIsMiniAppUser(true);
          sessionStorage.setItem("miniapp_login_check_done", "true");

          setTimeout(() => {
            passwordInputRef.current?.focus();
          }, 100);
        }
      } catch (err) {
        console.error("MiniApp: Failed to get user data:", err);
        sessionStorage.setItem("miniapp_login_check_done", "true");
      }
    };

    handleMiniAppLogin();
  }, [navigate]);

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      window.location.href = `${
        import.meta.env.VITE_API_BASE_URL
      }/auth/google?r=${import.meta.env.VITE_DOMAIN_NAME}`;
    } catch (errorCatch: any) {
      console.error("Google login error:", errorCatch);
      NetmeraLoginFailedEvent(
        "google",
        errorCatch?.message || t("error.somethingWentWrong"),
      );
      mixpanel.track("user_logged_in_failed", {
        login_method: "google_login",
        failure_reason: errorCatch?.message || t("error.somethingWentWrong"),
      });
      error(errorCatch.message || t("error.somethingWentWrong"), {
        title: t("auth.loginError"),
      });
      setIsLoading(false);
    }
  };

  const handleOmuzLogin = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        client_id: import.meta.env.VITE_OMUZ_CLIENT_ID,
        response_type: "code",
        redirect_uri: import.meta.env.VITE_OMUZ_REDIRECT_URI,
        scope: "profile email",
      });
      window.location.href = `${
        import.meta.env.VITE_OMUZ_OAUTH_URL
      }?${params.toString()}`;
    } catch (errorCatch: any) {
      console.error("Omuz login error:", errorCatch);
      NetmeraLoginFailedEvent(
        "omuz",
        errorCatch?.message || t("error.somethingWentWrong"),
      );
      mixpanel.track("user_logged_in_failed", {
        login_method: "omuz_login",
        failure_reason: errorCatch?.message || t("error.somethingWentWrong"),
      });
      error(errorCatch.message || t("error.somethingWentWrong"), {
        title: t("auth.loginError"),
      });
      setIsLoading(false);
    }
  };

  // const handleFacebookLogin = async () => {
  //   setIsLoading(true);
  //   setTimeout(() => {
  //     setIsLoading(false);
  //     info(t("auth.featureComingSoon"), {
  //       title: t("auth.notImplemented"),
  //     });
  //   }, 1000);
  // };

  const handleCredentialsLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    setIsLoading(true);

    type LoginApiResponse = {
      success?: boolean;
      message?: string;
      statusCode?: number;
      code?: string;
      data?: {
        accessToken?: string;
        refreshToken?: string;
        isVerified?: boolean;
        isVerificationExpired?: boolean;
      };
    };

    const getProviderFromMessage = (
      backendMessage?: string,
    ): string | undefined => {
      if (typeof backendMessage !== "string") return undefined;
      // Example: "You are registered with {provider}..."
      const match = backendMessage.match(/registered with\s+([a-z0-9_-]+)/i);
      return match?.[1];
    };

    const getLoginErrorMessage = (
      code?: string,
      backendMessage?: string,
    ): string => {
      const normalizedCode = typeof code === "string" ? code.toLowerCase() : "";
      const normalizedMessage =
        typeof backendMessage === "string" ? backendMessage.toLowerCase() : "";

      if (normalizedCode === "email_not_whitelisted") {
        return t("auth.errors.emailNotWhitelisted");
      }

      if (
        normalizedCode === "user_not_found" ||
        normalizedMessage.includes("user not found")
      ) {
        return t("auth.errors.userNotFound");
      }

      if (normalizedCode === "password_not_set") {
        return t("auth.errors.passwordNotSet");
      }

      if (normalizedCode === "user_deleted") {
        return t("auth.errors.userDeleted");
      }

      if (normalizedCode === "oauth_login_required") {
        const provider = getProviderFromMessage(backendMessage);
        return t("auth.errors.oauthLoginRequired", {
          provider: provider || "OAuth",
        });
      }

      if (
        normalizedCode === "password_mismatch" ||
        normalizedCode === "wrong_password" ||
        normalizedCode === "invalid_credentials" ||
        normalizedMessage.includes("password_mismatch") ||
        normalizedMessage.includes("wrong password") ||
        normalizedMessage.includes("invalid credentials") ||
        normalizedMessage.includes("incorrect password")
      ) {
        return t("auth.errors.invalidCredentials");
      }

      return backendMessage || t("auth.errors.loginFailed");
    };

    const handleEmailNotVerifiedFlow = async () => {
      try {
        await apiClient.post(API_ENDPOINTS.resendVerificationCode, {
          email,
        });

        info(t("auth.verificationCodeSent", { email }), {
          title: t("auth.emailNotVerified"),
          duration: 10000,
        });
      } catch (resendErrorResponse: any) {
        console.error(
          "Verification code gönderme hatası:",
          resendErrorResponse,
        );
        const rateLimitMessage = resendErrorResponse?.response?.data?.code as
          | string
          | undefined;

        if (
          typeof rateLimitMessage === "string" &&
          rateLimitMessage.toLowerCase().includes("please wait")
        ) {
          warning(rateLimitMessage, {
            title: t("auth.emailNotVerified"),
            duration: 10000,
          });
        } else {
          info(t("auth.redirectingToVerification"), {
            title: t("auth.emailNotVerified"),
          });
        }
      }

      navigate("/verify-email", { state: { email } });
    };

    try {
      const response = await apiClient.post<LoginApiResponse>(
        API_ENDPOINTS.login,
        {
          email,
          password,
        },
      );

      // ✅ IMPORTANT: api-client interceptor'ı 4xx hataları "resolve" edebiliyor.
      // Bu yüzden burada response shape'ini kontrol edip backend error'larını handle ediyoruz.
      const backendMessage =
        typeof response?.message === "string" ? response.message : undefined;
      const backendCode =
        typeof response?.code === "string" ? response.code : undefined;
      const normalizedCode =
        typeof backendCode === "string" ? backendCode.toLowerCase() : "";
      const normalizedMessage =
        typeof backendMessage === "string" ? backendMessage.toLowerCase() : "";

      // Email doğrulanmadı: Mesaj değişse bile code'a göre yakala
      if (
        normalizedCode === "email_not_verified" ||
        (response?.statusCode === 403 &&
          normalizedMessage.includes("verify your email"))
      ) {
        NetmeraLoginFailedEvent("credentials", "Email not verified");
        mixpanel.track("user_logged_in_failed", {
          login_method: "credentials_login",
          failure_reason: "Email not verified",
        });
        await handleEmailNotVerifiedFlow();
        return;
      }

      if (response?.data?.accessToken) {
        const token = response.data.accessToken;
        const refreshToken = response.data.refreshToken || null;
        setTokens({ accessToken: token, refreshToken });

        try {
          const userResponse = await apiClient.get<{
            success: boolean;
            message: string;
            data: {
              _id: string;
              name: string;
              email: string;
              image: string | null;
              isVerified: boolean;
              verificationCode: string | null;
              verificationCodeExpiresAt: string | null;
              lastVerificationSentAt: string;
              role: string;
              token: number;
              theme: string;
              language: string;
              isDeleted: boolean;
              createdAt: string;
              updatedAt: string;
              __v: number;
              addresses: Array<{
                label: string;
                addressLine1: string;
                addressLine2: string;
                city: string;
                state: string;
                postalCode: string;
                country: string;
                _id: string;
              }>;
              maxTokens: {
                subscription: number;
                oneTime: number;
                _id: string;
              };
              stripeCustomerId: string;
              tokens: {
                subscription: number;
                oneTime: number;
                _id: string;
              };
            };
          }>(API_ENDPOINTS.getMe);

          if (userResponse.data) {
            setUser(userResponse.data);
            Sentry.setUser({
              id: userResponse.data._id,
              email: userResponse.data.email,
            });
            try {
              mixpanel.identify(userResponse.data._id);
              mixpanel.people.set({
                $name: userResponse.data.name,
                $email: userResponse.data.email,
                $created: userResponse.data.createdAt,
              });
              mixpanel.track("user_logged_in", {
                type: "credentials_login",
                email: userResponse.data.email,
                name: userResponse.data.name,
                _id: userResponse.data._id,
              });
            } catch (e) {
              console.warn("mx error", e);
            }
            window.netmera.push((api) => {
              const user = api.getUser();
              user.setName(userResponse.data.name);
              user.setEmail(userResponse.data.email);
              user.setCustomId(userResponse.data._id);
              user.save();

              api.sendEvent({
                code: "n:cl",
                uid: userResponse.data._id,
              });
            });
            success(
              t("auth.welcomeBack", {
                name: userResponse.data.name,
              }),
              {
                title: t("auth.loginSuccess"),
              },
            );
            const slackRedirect = sessionStorage.getItem("slack_link_redirect");
            const authGateRedirect = consumeAuthGateRedirect();
            if (slackRedirect) {
              sessionStorage.removeItem("slack_link_redirect");
              window.location.href = slackRedirect;
            } else if (authGateRedirect) {
              // Auth gate popup'ından gelen kullanıcıyı kaldığı sayfaya geri götür
              navigate(authGateRedirect);
            } else {
              // Kullanıcı login'e nereden geldiyse oraya dön (yoksa ana sayfa)
              navigate(readLastVisitedRoute() ?? "/");
            }
          } else {
            throw new Error("Failed to get user profile");
          }
        } catch (userError) {
          setTokens({ accessToken: "", refreshToken: null });
          throw userError;
        }
      } else {
        console.log("auth.errors.loginFailed");
        NetmeraLoginFailedEvent(
          "credentials",
          getLoginErrorMessage(backendCode, backendMessage),
        );
        mixpanel.track("user_logged_in_failed", {
          login_method: "credentials_login",
          failure_reason: getLoginErrorMessage(backendCode, backendMessage),
        });
        error(getLoginErrorMessage(backendCode, backendMessage), {
          title: t("auth.loginError"),
        });
        return;
      }
    } catch (errorCatch: any) {
      const backendMessage =
        errorCatch?.response?.data?.message ||
        (typeof errorCatch?.message === "string"
          ? errorCatch.message
          : undefined);
      const backendCode =
        typeof errorCatch?.response?.data?.code === "string"
          ? errorCatch.response.data.code
          : undefined;
      const normalizedCode =
        typeof backendCode === "string" ? backendCode.toLowerCase() : "";
      const normalizedMessage =
        typeof backendMessage === "string" ? backendMessage.toLowerCase() : "";

      if (
        normalizedCode === "email_not_verified" ||
        (errorCatch?.response?.data?.statusCode === 403 &&
          normalizedMessage.includes("verify your email"))
      ) {
        NetmeraLoginFailedEvent("credentials", "Email not verified");
        await handleEmailNotVerifiedFlow();
        return;
      }

      NetmeraLoginFailedEvent(
        "credentials",
        getLoginErrorMessage(backendCode, backendMessage),
      );
      error(getLoginErrorMessage(backendCode, backendMessage), {
        title: t("auth.loginError"),
      });
    } finally {
      isSubmitting.current = false;
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <AuthStage>
          <div className={authStackClass}>
            {/* Header */}
            <div className="flex flex-col items-start gap-1.5">
              <h1 className={authTitleClass}>
                {t("auth.login")}
              </h1>
              <div className={authSubtitleClass}>
                <span>{t("auth.dontHaveAccount")}</span>
                <Link
                  to="/register"
                  className={authLinkClass}
                >
                  {t("auth.register")}
                </Link>
              </div>
            </div>

            {/* MiniApp Message */}
            {isMiniAppUser && (
              <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm text-foreground">
                  {t("auth.miniappLoginMessage")}
                </p>
              </div>
            )}

            {/* Social - Hidden in MiniApp */}
            {!miniAppBridgeService.isInMiniApp() && (
              <>
                <div className={authSocialStackClass}>
                  <SocialButton
                    onClick={handleGoogleLogin}
                    icon={<GoogleBrandIcon className="h-5 w-5" />}
                    label={t("auth.continueWithGoogle")}
                    visuallyDisabled={isLoading}
                  />
                  {import.meta.env.VITE_APP_ENV === "dev" && (
                    <SocialButton
                      onClick={handleAppleLogin}
                      icon={<AppleBrandIcon className="h-5 w-5" />}
                      label={t("auth.continueWithApple")}
                      visuallyDisabled={isLoading || isAppleLoading}
                    />
                  )}
                  {import.meta.env.VITE_APP_ENV === "dev" && (
                    <SocialButton
                      onClick={handleOmuzLogin}
                      icon={<OmuzLogo className="h-5 w-5 text-[#FA6B17]" />}
                      label={t("auth.continueWithOmuz")}
                      visuallyDisabled={isLoading}
                    />
                  )}
                </div>

                <FormDivider text={t("common.or")} />
              </>
            )}

            {/* Form */}
            <form onSubmit={handleCredentialsLogin} className={authFormClass}>
              <div className="space-y-1">
                <label
                  htmlFor="email"
                  className={authLabelClass}
                >
                  {t("auth.email")}
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("auth.enterEmail")}
                    className={`${authFieldClass} pl-9`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    aria-invalid={email.length > 0 && !email.includes("@")}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="password"
                  className={authLabelClass}
                >
                  {t("auth.password")}
                </label>
                <div className="relative">
                  <LockIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={passwordInputRef}
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("auth.enterPassword")}
                    className={`${authFieldClass} pl-9 pr-10`}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    aria-invalid={password.length > 0 && password.length < 8}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-2.5 text-muted-foreground"
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-start">
                <Link
                  to="/forgot-password"
                  className={`${authLinkClass} text-sm`}
                >
                  {t("auth.forgotPassword")}
                </Link>
              </div>

              <Button
                type="submit"
                variant="darkPrimary"
                className={authPrimaryButtonClass}
                visuallyDisabled={isLoading}
              >
                {isLoading ? t("common.loading") : t("auth.login")}
              </Button>
            </form>

            {/* Destek çıkışı + Language/Theme Switcher */}
            <div className={authFooterClass}>
              <AuthSupportLink />
            </div>
          </div>
    </AuthStage>
  );
}
